import type { MaterialSymbol } from "material-symbols";
import { useEffect, useState } from "react";
import { sortAlpha } from "widgets/utils";

import { SupportedExecutorsEnum } from "@/graphql.ts";
import { getApplicationConfig } from "@/modules/app/utils/get-configuration";
import { getComputingIcon, getComputingLabel } from "@/modules/workflow/utils/computing.ts";

interface Option {
  key: SupportedExecutorsEnum | "default";
  label: string;
  icon: MaterialSymbol;
}

const defaultOption = {
  key: "default",
  label: getComputingLabel("default"),
  icon: getComputingIcon(null),
} satisfies Option;

/**
 * We don't sort by default because order matters at the activity level
 * @returns [options, disabledOptions]
 */
export function useComputing(executors: SupportedExecutorsEnum[] = [], sort = false, noDefault = false) {
  const [options, setOptions] = useState<Option[]>([]);
  const [computingDisabledKeys, setComputingDisabledKeys] = useState<SupportedExecutorsEnum[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(undefined);

        // enum to string
        let computingOptions = executors
          .map((key) => {
            return {
              key,
              label: getComputingLabel(key),
              icon: getComputingIcon(key),
            } satisfies Option;
          })
          .filter((v: Option): v is Option => v != undefined);

        if (sort) {
          computingOptions = computingOptions.sort((a, b) => sortAlpha(a.label, b.label));
        }

        const configuration = await getApplicationConfig();

        const disabledKeys: SupportedExecutorsEnum[] = [];
        if (!configuration?.hpcEnabled) {
          disabledKeys.push(SupportedExecutorsEnum.Cluster);
        }

        if (!configuration?.cloudEnabled) {
          disabledKeys.push(SupportedExecutorsEnum.Aws);
        }

        // Update values at the same times as they belong together.
        setOptions(noDefault ? computingOptions : [defaultOption, ...computingOptions]);
        setComputingDisabledKeys(disabledKeys);
      } catch (error) {
        setError(error as Error);
      }
    }

    load().finally(() => setLoading(false));
  }, [executors, noDefault, sort]);

  return { options, computingDisabledKeys, loading, error };
}
